// ---------------------------------------------------------------------------
// Design ▸ Tokens — the colours and variables the imported design file carries.
//
// Every number on this screen is read straight off the import; nothing is
// filled in. A variable with no recorded use says so, a file with no variables
// at all says so, and the caption under the usage column states exactly what
// kind of evidence produced the link instead of implying a declared binding.
// ---------------------------------------------------------------------------

import { Check, Clock3, Palette, Search, X } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import type { OpenPencilFigmaSourceBundle } from "./openpencil-figma-source";
import {
  buildSourceTokenIndex,
  countSourceTokens,
  filterSourceTokenGroups,
  type SourceTokenRow,
} from "./openpencil-source-library-model";
import type {
  OpenPencilSourceWorkspace,
  SourceDecisionStatus,
} from "./openpencil-source-workspace";

/** Human wording for the variable types the import can emit. */
const TYPE_LABEL: Record<string, string> = {
  color: "Colour",
  typography: "Typography",
  spacing: "Spacing",
  radius: "Corner radius",
  shadow: "Shadow",
  boolean: "True/false",
  string: "Text",
  number: "Number",
  unknown: "Other",
};

function typeLabel(tokenType: string): string {
  return TYPE_LABEL[tokenType] ?? tokenType;
}

function Section({
  title,
  caption,
  action,
  testId,
  children,
}: {
  title: string;
  caption?: ReactNode;
  action?: ReactNode;
  testId: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-lg border border-border bg-card"
      data-testid={testId}
    >
      <header className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-2">
        <div className="flex min-w-0 flex-col">
          <h2 className="truncate text-sm font-semibold text-foreground">
            {title}
          </h2>
          {caption ? (
            <p className="text-[11px] leading-4 text-muted-foreground">
              {caption}
            </p>
          ) : null}
        </div>
        {action}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * A colour chip over a checkerboard, so a variable the file records as
 * transparent (`#00000000`) reads as transparent instead of as white.
 */
function Swatch({ color, size = "md" }: { color: string; size?: "sm" | "md" }) {
  return (
    <span
      className={`inline-block shrink-0 overflow-hidden rounded-sm border border-border ${
        size === "sm" ? "h-3.5 w-3.5" : "h-6 w-6"
      }`}
      style={{
        backgroundImage:
          "linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%), linear-gradient(45deg, hsl(var(--muted)) 25%, transparent 25%, transparent 75%, hsl(var(--muted)) 75%)",
        backgroundSize: "6px 6px",
        backgroundPosition: "0 0, 3px 3px",
      }}
      data-testid="openpencil-token-swatch"
      data-color={color}
      aria-hidden
    >
      <span
        className="block h-full w-full"
        style={{ backgroundColor: color }}
      />
    </span>
  );
}

function DecisionButtons({
  label,
  status,
  onChange,
}: {
  label: string;
  status: SourceDecisionStatus;
  onChange: (next: SourceDecisionStatus) => void;
}) {
  const items = [
    { status: "accepted" as const, icon: Check, title: `Keep ${label}` },
    { status: "pending" as const, icon: Clock3, title: `Decide ${label} later` },
    { status: "rejected" as const, icon: X, title: `Drop ${label}` },
  ];
  return (
    <div className="inline-flex h-7 shrink-0 overflow-hidden rounded-md border border-border bg-background">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.status}
            type="button"
            title={item.title}
            aria-label={item.title}
            aria-pressed={status === item.status}
            onClick={() => onChange(item.status)}
            className={`flex h-7 w-7 items-center justify-center border-l border-border first:border-l-0 ${
              status === item.status
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Icon className="h-3 w-3" />
          </button>
        );
      })}
    </div>
  );
}

/**
 * Roles a source colour can take, in the app the project will generate.
 *
 * These are the renderer's own CSS-variable names (`COLOR_KEY_TO_CSS`,
 * packages/cogo-react-renderer/src/runtime/design-tokens.ts:131) — deliberately
 * not a longer, friendlier list, because a role the renderer does not know is a
 * choice that silently does nothing.
 *
 * Until this control existed, `canonicalToken` could only be set by an agent
 * calling the MCP tool: the panel read the stored value and passed it straight
 * back. So a human could accept 275 variables and still be told to "map an
 * accepted token to primary, background, foreground" with no control that
 * mapped anything — the imported design could not become a design system at
 * all unless its variables happened to be *named* `primary`/`background`/
 * `foreground`. Real files (`base/base/blue/500`, `element base/background/
 * background`) never are.
 */
export const SOURCE_TOKEN_SEMANTIC_ROLES = [
  { value: 'primary', label: 'Main colour', required: true },
  { value: 'background', label: 'Page background', required: true },
  { value: 'foreground', label: 'Body text', required: true },
  { value: 'primary-foreground', label: 'Text on main colour' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'secondary-foreground', label: 'Text on secondary' },
  { value: 'muted', label: 'Muted surface' },
  { value: 'muted-foreground', label: 'Muted text' },
  { value: 'accent', label: 'Accent surface' },
  { value: 'accent-foreground', label: 'Text on accent' },
  { value: 'card', label: 'Card surface' },
  { value: 'card-foreground', label: 'Card text' },
  { value: 'popover', label: 'Popover surface' },
  { value: 'popover-foreground', label: 'Popover text' },
  { value: 'border', label: 'Border' },
  { value: 'input', label: 'Input border' },
  { value: 'ring', label: 'Focus ring' },
  { value: 'destructive', label: 'Destructive' },
  { value: 'destructive-foreground', label: 'Text on destructive' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
  { value: 'info', label: 'Info' },
] as const;

function TokenRoleSelect({
  token,
  role,
  onAssign,
}: {
  token: SourceTokenRow;
  role: string | undefined;
  onAssign: (role: string | undefined) => void;
}) {
  return (
    <label
      className="shrink-0 text-[11px] text-muted-foreground"
      title={`Give ${token.name} a role in the generated app`}
    >
      <span className="sr-only">Role for {token.name}</span>
      <select
        data-testid="openpencil-token-role"
        data-token-id={token.id}
        value={role ?? ''}
        onChange={(event) =>
          onAssign(event.currentTarget.value || undefined)
        }
        className="h-7 max-w-[9.5rem] rounded-md border border-border bg-background px-1.5 text-[11px] text-foreground outline-none focus:border-foreground"
      >
        <option value="">No role</option>
        {SOURCE_TOKEN_SEMANTIC_ROLES.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {'required' in entry && entry.required
              ? `${entry.label} ★`
              : entry.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TokenRow({
  token,
  status,
  role,
  onDecision,
  onAssignRole,
  usageEvidenceAvailable,
}: {
  token: SourceTokenRow;
  status: SourceDecisionStatus;
  role?: string | undefined;
  onDecision?: (next: SourceDecisionStatus) => void;
  onAssignRole?: (role: string | undefined) => void;
  usageEvidenceAvailable: boolean;
}) {
  const showModes = token.modes.length > 1;
  return (
    <li
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border py-2.5 last:border-b-0"
      data-testid="openpencil-token-row"
      data-token-id={token.id}
    >
      {token.swatch ? (
        <Swatch color={token.swatch} />
      ) : (
        <span
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm border border-dashed border-border text-[9px] uppercase text-muted-foreground"
          aria-hidden
        >
          {token.tokenType.slice(0, 2)}
        </span>
      )}
      <div className="min-w-0 flex-1 basis-40">
        <div
          className="truncate text-sm font-medium text-foreground"
          title={token.name}
        >
          {token.shortName}
        </div>
        <div
          className="truncate text-[11px] text-muted-foreground"
          title={token.name}
        >
          {token.name}
        </div>
      </div>
      {showModes ? (
        <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-1.5">
          {token.modes.map((mode) => (
            <span
              key={mode.modeId}
              className="inline-flex items-center gap-1 rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground"
              data-testid="openpencil-token-mode"
              data-mode={mode.modeName}
            >
              {mode.swatch ? <Swatch color={mode.swatch} size="sm" /> : null}
              <span className="font-medium text-foreground">
                {mode.modeName}
              </span>
              <span className="font-mono">
                {mode.display ?? mode.aliasName ?? "no value"}
              </span>
            </span>
          ))}
        </div>
      ) : (
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
          {token.displayValue ?? "no value"}
        </span>
      )}
      <span
        className="shrink-0 text-[11px] text-muted-foreground"
        data-testid="openpencil-token-usage"
        title={
          token.usedBy.length > 0
            ? token.usedBy
                .map((ref) => `${ref.componentSetName} (${ref.usageCount})`)
                .join("\n")
            : undefined
        }
      >
        {!usageEvidenceAvailable
          ? "no component data"
          : token.usedBy.length === 0
            ? "no match"
            : `${token.usedBy.length} component${token.usedBy.length === 1 ? "" : "s"}`}
      </span>
      {onDecision ? (
        <DecisionButtons
          label={token.name}
          status={status}
          onChange={onDecision}
        />
      ) : (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {status}
        </span>
      )}
      {onAssignRole && token.tokenType === 'color' ? (
        <TokenRoleSelect token={token} role={role} onAssign={onAssignRole} />
      ) : null}
    </li>
  );
}

interface Props {
  bundle: OpenPencilFigmaSourceBundle;
  workspace?: OpenPencilSourceWorkspace | null;
  onTokenDecision?: (
    tokenId: string,
    status: SourceDecisionStatus,
    canonicalToken?: string,
  ) => void;
  onBulkTokenDecision?: (
    status: Exclude<SourceDecisionStatus, "pending">,
  ) => void;
  /** Offered only when a saved project can actually receive the registration. */
  onRegisterProjectDesignSystem?: () => void;
  registerProjectDesignSystemPending?: boolean;
  projectDesignSystemNotice?: string | null;
  registeredProjectDesignSystemId?: string | null;
}

export function OpenPencilSourceTokens({
  bundle,
  workspace,
  onTokenDecision,
  onBulkTokenDecision,
  onRegisterProjectDesignSystem,
  registerProjectDesignSystemPending,
  projectDesignSystemNotice,
  registeredProjectDesignSystemId,
}: Props) {
  const [query, setQuery] = useState("");
  const index = useMemo(() => buildSourceTokenIndex(bundle), [bundle]);
  const groups = useMemo(
    () => filterSourceTokenGroups(index.groups, query),
    [index.groups, query],
  );
  const matched = countSourceTokens(groups);
  const decisions = workspace?.tokenDecisions;
  const acceptedCount = useMemo(
    () =>
      Object.values(decisions ?? {}).filter(
        (decision) => decision.status === "accepted",
      ).length,
    [decisions],
  );

  const colorQuery = query.trim().toLowerCase();
  const colorSamples = useMemo(
    () =>
      colorQuery
        ? index.colorSamples.filter((sample) =>
            sample.value.toLowerCase().includes(colorQuery),
          )
        : index.colorSamples,
    [colorQuery, index.colorSamples],
  );

  if (index.totalTokens === 0 && index.colorSamples.length === 0) {
    return (
      <div
        className="flex min-h-[260px] items-center justify-center rounded-lg border border-dashed border-border bg-card/40 px-6 py-10 text-center"
        data-testid="openpencil-tokens-empty"
      >
        <div className="max-w-sm">
          <Palette
            className="mx-auto h-8 w-8 text-muted-foreground"
            aria-hidden
          />
          <h2 className="mt-4 text-sm font-semibold text-foreground">
            No colours or variables in this design file
          </h2>
          <p className="mt-2 text-xs leading-5 text-muted-foreground">
            Studio only shows what it can read out of the file you imported.
            This one declared no variables and no colour fills. Add variables in
            your design tool and import the file again to see them here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="openpencil-tokens-view">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 basis-56 items-center gap-2 rounded-md border border-border bg-background px-2">
            <Search
              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
              aria-hidden
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              placeholder="Find a colour or variable by name or value"
              aria-label="Find a colour or variable"
              data-testid="openpencil-tokens-search"
              className="h-9 min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
            />
          </div>
          <dl className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <div className="flex items-baseline gap-1.5">
              <dt className="text-muted-foreground">Variables</dt>
              <dd
                className="font-semibold text-foreground"
                data-testid="openpencil-tokens-total"
              >
                {index.totalTokens.toLocaleString()}
              </dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt className="text-muted-foreground">Colours in the file</dt>
              <dd
                className="font-semibold text-foreground"
                data-testid="openpencil-tokens-color-total"
              >
                {index.colorSamples.length.toLocaleString()}
              </dd>
            </div>
            {index.modeNames.length > 0 ? (
              <div className="flex items-baseline gap-1.5">
                <dt className="text-muted-foreground">Modes</dt>
                <dd className="font-semibold text-foreground">
                  {index.modeNames.join(" · ")}
                </dd>
              </div>
            ) : null}
          </dl>
          {onBulkTokenDecision ? (
            <div className="ml-auto flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onBulkTokenDecision("accepted")}
                className="h-8 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-foreground hover:bg-muted"
              >
                Keep all
              </button>
              <button
                type="button"
                onClick={() => onBulkTokenDecision("rejected")}
                className="h-8 rounded-md border border-border bg-background px-2.5 text-[11px] font-medium text-foreground hover:bg-muted"
              >
                Drop all
              </button>
            </div>
          ) : null}
        </div>
        {query ? (
          <p
            className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground"
            data-testid="openpencil-tokens-match-count"
          >
            {matched.toLocaleString()} of {index.totalTokens.toLocaleString()}{" "}
            variables and {colorSamples.length.toLocaleString()} of{" "}
            {index.colorSamples.length.toLocaleString()} colours match
            “{query}”.
          </p>
        ) : null}
        {/*
          Reported defect, measured on this screen 2026-07-31: applying the
          kept tokens to the project — the one action that makes any of the 275
          rows below matter — was an 11px underlined run of text inside a
          muted 17px-tall line ("275 kept for this project. Apply these to this
          project"), sitting under two 8px-tall "Keep all" / "Drop all"
          buttons that only change a counter. The decision the whole screen
          exists to reach was the smallest thing on it. It gets the weight of
          what it does: a real button, at the size of a primary action, with
          the count it acts on stated next to it.
        */}
        {workspace ? (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border px-4 py-3"
            data-testid="openpencil-tokens-apply-bar"
          >
            {onRegisterProjectDesignSystem ? (
              <button
                type="button"
                disabled={
                  registerProjectDesignSystemPending || acceptedCount === 0
                }
                onClick={onRegisterProjectDesignSystem}
                data-testid="openpencil-tokens-apply-button"
                className="inline-flex h-9 items-center rounded-md bg-primary px-3.5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {registerProjectDesignSystemPending
                  ? "Applying…"
                  : registeredProjectDesignSystemId
                    ? "Re-apply to this project"
                    : "Apply these to this project"}
              </button>
            ) : null}
            <p className="text-xs text-muted-foreground">
              {acceptedCount.toLocaleString()} kept for this project
              {acceptedCount === 0
                ? " — keep at least one variable to apply."
                : "."}
            </p>
            {projectDesignSystemNotice ? (
              <p
                className="basis-full text-xs leading-5 text-muted-foreground"
                data-testid="openpencil-tokens-apply-notice"
              >
                {projectDesignSystemNotice}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      {index.totalTokens > 0 && groups.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-border bg-card/40 px-4 py-8 text-center text-xs text-muted-foreground"
          data-testid="openpencil-tokens-no-match"
        >
          No variable matches “{query}”. Clear the search to see all{" "}
          {index.totalTokens.toLocaleString()}.
        </div>
      ) : null}

      {groups.map((group) => (
        <Section
          key={group.key}
          testId="openpencil-token-group"
          title={`${group.collectionLabel} · ${typeLabel(group.tokenType)}`}
          caption={
            <>
              {group.tokens.length.toLocaleString()} variable
              {group.tokens.length === 1 ? "" : "s"}
              {group.modeNames.length > 1
                ? ` · ${group.modeNames.join(" / ")}`
                : ""}
            </>
          }
        >
          <ul className="max-h-[420px] overflow-auto">
            {group.tokens.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                usageEvidenceAvailable={index.usageEvidenceAvailable}
                status={decisions?.[token.id]?.status ?? "pending"}
                role={decisions?.[token.id]?.canonicalToken}
                {...(onTokenDecision
                  ? {
                      onDecision: (next: SourceDecisionStatus) =>
                        onTokenDecision(
                          token.id,
                          next,
                          decisions?.[token.id]?.canonicalToken,
                        ),
                      // Choosing a role IS an acceptance: a token nobody
                      // accepted cannot reach the design system, so a role
                      // picked on a pending row would look applied and do
                      // nothing. Clearing the role leaves the status alone.
                      onAssignRole: (role: string | undefined) =>
                        onTokenDecision(
                          token.id,
                          role
                            ? "accepted"
                            : (decisions?.[token.id]?.status ?? "pending"),
                          role,
                        ),
                    }
                  : {})}
              />
            ))}
          </ul>
        </Section>
      ))}

      {index.totalTokens > 0 ? (
        <p
          className="px-1 text-[11px] leading-4 text-muted-foreground"
          data-testid="openpencil-tokens-usage-caption"
        >
          {index.usageEvidenceAvailable
            ? `${index.tokensWithUsage.toLocaleString()} of ${index.totalTokens.toLocaleString()} variables were matched to a component because the component paints that exact colour${
                index.usageEvidenceKinds.length > 0
                  ? ` (${index.usageEvidenceKinds.join(", ")})`
                  : ""
              }. This design file records no explicit variable-to-layer bindings, so the remaining variables are shown without a usage link rather than guessed at.`
            : "This design file carries no component data, so where each variable is used could not be read."}
        </p>
      ) : null}

      <Section
        testId="openpencil-token-colors"
        title="Colours found in the artwork"
        caption={
          index.colorSamples.length > 0
            ? `${index.colorSamples.length.toLocaleString()} distinct fills, most used first`
            : "None recorded"
        }
      >
        {colorSamples.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {index.colorSamples.length === 0
              ? "The import found no colour fills in this file."
              : `No colour matches “${query}”.`}
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {colorSamples.map((sample) => (
              <li
                key={sample.id}
                className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5"
                data-testid="openpencil-token-color-sample"
              >
                {sample.swatch ? (
                  <Swatch color={sample.swatch} />
                ) : (
                  <span
                    className="h-6 w-6 shrink-0 rounded-sm border border-dashed border-border"
                    aria-hidden
                  />
                )}
                <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
                  {sample.label}
                </span>
                <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                  {sample.usageCount.toLocaleString()}×
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
